import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrintRows } from 'hooks/usePrintRows';
import { ApplicationState } from 'store';
import { AiManagementBoardTask, AiManagementState } from 'store/modules/ai_management_store/types';
import { getTasks } from 'store/modules/ai_management_store/actions';
import { taskStateOf } from 'services/ai_management_task_state';
import { formatDate } from 'services/ai_management_dates';
import Pagination from 'components/atoms/pagination';
import ItemShare from 'components/atoms/item_share';
import AiManagementCardMenu from 'components/molecules/ai_management_card_menu';
import AiManagementTaskDrawer from 'components/organisms/ai_management_task_drawer';
import { Container } from './styles';

const PAGE_SIZE = 10;

const AiManagementTasksList: React.FC = () => {
  const dispatch = useDispatch();
  const { taskColumns, tasks, request } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showShareWindow, setShowShareWindow] = useState(false);
  const [page, setPage] = useState(1);
  const [openTask, setOpenTask] = useState<AiManagementBoardTask | null>(null);
  const { printRef, printingSelection, print } = usePrintRows({
    documentTitle: 'Gestão IA - Tarefas',
    hasSelection: selected.size > 0,
  });

  useEffect(() => {
    dispatch(getTasks());
  }, [dispatch]);

  const rows = useMemo(() => {
    const orderedColumns = [...taskColumns].sort(
      (a, b) => a.sort_group - b.sort_group || a.position - b.position,
    );
    return orderedColumns.flatMap((column) =>
      [...(tasks[column.id] ?? [])].sort((a, b) => a.position - b.position),
    );
  }, [taskColumns, tasks]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageIds = pageRows.map((task) => task.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const visibleRows = printingSelection ? rows.filter((task) => selected.has(task.id)) : pageRows;
  const selectedBiddingIds = Array.from(
    new Set(rows.filter((task) => selected.has(task.id)).map((task) => task.bidding.id)),
  );

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  if (!request.tasks.loaded && !request.tasks.error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (request.tasks.error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as tarefas.</p>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => dispatch(getTasks())}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <Container ref={printRef}>
      <h5 className="d-none d-print-block mb-3">Gestão IA - Tarefas</h5>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 d-print-none">
        <label className="mb-0 d-flex align-items-center" htmlFor="select-all-tasks-page">
          <input
            id="select-all-tasks-page"
            type="checkbox"
            className="mr-2"
            checked={allPageSelected}
            onChange={toggleAllPage}
            disabled={pageIds.length === 0}
          />
          Selecionar todas as tarefas da página
        </label>

        <div className="d-flex position-relative">
          <button
            type="button"
            className="btn btn-secondary btn-sm mr-2"
            disabled={selected.size === 0}
          >
            Ações
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm mr-2"
            disabled={selected.size === 0}
            onClick={() => setShowShareWindow((open) => !open)}
          >
            Compartilhar
          </button>

          {showShareWindow && (
            <ItemShare setShowShareWindow={setShowShareWindow} selectedItems={selectedBiddingIds} />
          )}
          <AiManagementCardMenu
            ariaLabel="Exportar"
            label="Exportar"
            disabled={selected.size === 0}
            items={[
              { label: 'Gerar .xlsx', onClick: () => {} },
              { label: 'Gerar .docx', onClick: () => {} },
              { label: 'Imprimir', onClick: () => print?.() },
            ]}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table tasks-table">
          <thead>
            <tr>
              <th style={{ width: 40 }} className="d-print-none" aria-label="Selecionar" />
              <th aria-label="Contadores" />
              <th>Data</th>
              <th>Status</th>
              <th>Tarefa</th>
              <th>Órgão e Edital</th>
              <th>Responsável</th>
              <th style={{ width: 40 }} className="d-print-none" aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  Nenhuma tarefa encontrada.
                </td>
              </tr>
            )}
            {visibleRows.map((task) => {
              const { label, badge } = taskStateOf(task);
              return (
                <tr
                  key={task.id}
                  className="tasks-row"
                  onClick={() => setOpenTask(task)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setOpenTask(task);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Abrir ${task.title}`}
                >
                  <td className="d-print-none">
                    <input
                      type="checkbox"
                      checked={selected.has(task.id)}
                      onChange={() => toggleOne(task.id)}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Selecionar ${task.title}`}
                    />
                  </td>
                  <td className="tasks-counts text-nowrap">
                    <span className="task-count border rounded d-inline-flex align-items-center mr-1">
                      <i className="material-icons mr-1">attach_file</i>
                      {task.counts.attachments}
                    </span>
                    <span className="task-count border rounded d-inline-flex align-items-center">
                      <i className="material-icons mr-1">chat_bubble_outline</i>
                      {task.counts.comments}
                    </span>
                  </td>
                  <td className="text-nowrap">{task.deadline ? formatDate(task.deadline) : '—'}</td>
                  <td>
                    <span className={`badge badge-${badge}`}>{label}</span>
                  </td>
                  <td className="font-weight-bold text-dark">{task.title}</td>
                  <td>
                    <div className="tasks-bidding">
                      <div className="font-weight-bold text-dark">{task.bidding.notice_number}</div>
                      <div className="text-muted text-truncate">{task.bidding.public_body}</div>
                      <div className="text-muted">
                        {task.bidding.city} - {task.bidding.state}
                      </div>
                    </div>
                  </td>
                  <td>{task.responsible?.name ?? '—'}</td>
                  <td className="text-right d-print-none">
                    <span
                      className="btn btn-secondary btn-sm d-inline-flex align-items-center"
                      aria-hidden="true"
                    >
                      <i className="material-icons">chevron_right</i>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-print-none">
          <Pagination
            postsPerPage={PAGE_SIZE}
            totalPosts={rows.length}
            currentPage={currentPage}
            paginate={setPage}
            firstAndLastPage
          />
        </div>
      )}

      {openTask && <AiManagementTaskDrawer task={openTask} onClose={() => setOpenTask(null)} />}
    </Container>
  );
};

export default AiManagementTasksList;
